import React, { useContext, useState } from 'react';
import { ConfigurationContext } from '../DataContexts/ConfigurationContext';
import ConfigSection from './ConfigSection';
import { formatEuro, euroToCents, formatClockTime } from './configFormat';

const DEFAULT_TIER_COLORS = ['#cd7f32', '#c0c0c0', '#ffd700'];

const emptyForm = () => ({
  name: '',
  startTime: '00:00',
  tiers: [
    { startPrice: '', creditBonus: '', color: DEFAULT_TIER_COLORS[0] },
    { startPrice: '', creditBonus: '', color: DEFAULT_TIER_COLORS[1] },
    { startPrice: '', creditBonus: '', color: DEFAULT_TIER_COLORS[2] },
  ],
  tier3EndPrice: '',
});

const BonusModelCard = ({ model, onDelete }) => {
  // Each tier runs from its own start price up to the next tier's start
  // (tier 3 up to its explicit end price)
  const tierRows = [1, 2, 3].map((tier) => ({
    tier,
    from: model[`tier${tier}startprice`],
    to: tier === 3 ? model.tier3endprice : model[`tier${tier + 1}startprice`],
    bonus: model[`tier${tier}creditbonus`],
    color: model[`tier${tier}color`],
  }));

  return (
    <article className="config-mode-card">
      <header className="config-mode-card-header">
        <h3 className="config-mode-card-title">{model.name}</h3>
        <button
          type="button"
          className="config-danger-btn"
          onClick={() => onDelete(model)}
          aria-label={`Delete bonus model ${model.name}`}
        >
          Delete
        </button>
      </header>
      <table className="config-tier-table">
        <thead>
          <tr>
            <th scope="col">Tier</th>
            <th scope="col">Spend range</th>
            <th scope="col">Bonus</th>
            <th scope="col">Color</th>
          </tr>
        </thead>
        <tbody>
          {tierRows.map(({ tier, from, to, bonus, color }) => (
            <tr key={tier}>
              <td>Tier {tier}</td>
              <td>
                {formatEuro(from)} – {formatEuro(to)}
              </td>
              <td>+{bonus ?? 0} credits</td>
              <td>
                <span className="config-color-swatch" style={{ backgroundColor: color }} />
                <span className="config-color-value">{color || '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="config-muted">
        Bonus day starts at {formatClockTime(model.starthourofbonusday, model.startminuteofbonusday)}
      </p>
    </article>
  );
};

// DJB-163: overview of all bonus configuration models with create and delete.
// DJB-181: inline editing of existing models is removed — create a new model
// (and delete the old one) to change the tiers.
const BonusTiersSection = () => {
  const {
    bonusConfigurations,
    bonusConfigurationsLoading,
    createBonusConfiguration,
    deleteBonusConfiguration,
  } = useContext(ConfigurationContext);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setForm(emptyForm());
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormError(null);
  };

  const updateTier = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError('Give the bonus model a name.');
      return;
    }
    const startPrices = form.tiers.map((tier) => euroToCents(tier.startPrice));
    const bonuses = form.tiers.map((tier) => parseInt(tier.creditBonus, 10));
    const endPrice = euroToCents(form.tier3EndPrice);
    if (
      startPrices.some((price) => price == null || price < 0) ||
      endPrice == null ||
      endPrice < 0 ||
      bonuses.some((bonus) => !Number.isFinite(bonus) || bonus < 0)
    ) {
      setFormError('Every tier needs a start price and a credit bonus (tier 3 also an end price).');
      return;
    }
    if (!(startPrices[0] < startPrices[1] && startPrices[1] < startPrices[2] && startPrices[2] < endPrice)) {
      setFormError('Prices must increase: tier 1 < tier 2 < tier 3 < tier 3 end.');
      return;
    }
    const [startHour, startMinute] = form.startTime.split(':').map((part) => parseInt(part, 10));
    const payload = {
      name: form.name.trim(),
      tier1startprice: startPrices[0],
      tier2startprice: startPrices[1],
      tier3startprice: startPrices[2],
      tier3endprice: endPrice,
      tier1creditbonus: bonuses[0],
      tier2creditbonus: bonuses[1],
      tier3creditbonus: bonuses[2],
      starthourofbonusday: Number.isFinite(startHour) ? startHour : 0,
      startminuteofbonusday: Number.isFinite(startMinute) ? startMinute : 0,
      tier1color: form.tiers[0].color,
      tier2color: form.tiers[1].color,
      tier3color: form.tiers[2].color,
    };
    setFormError(null);
    setSaving(true);
    try {
      await createBonusConfiguration(payload);
      closeForm();
    } catch (error) {
      // notification shown by the context; keep the form open for a retry
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (model) => {
    const confirmed = window.confirm(`Are you sure you want to delete bonus model "${model.name}"?`);
    if (!confirmed) return;
    try {
      await deleteBonusConfiguration(model.id);
    } catch (error) {
      // notification shown by the context
    }
  };

  return (
    <ConfigSection
      anchorId="section-bonus-tiers"
      title="Bonus Tiers"
      actions={
        !showForm && (
          <button type="button" className="config-primary-btn" onClick={openCreate}>
            New bonus model
          </button>
        )
      }
    >
      <p className="config-section-intro">
        Daily spend tiers that grant bonus credits. A bonus model is assigned to a machine; the
        tier prices are the total spend since the start of the bonus day.
      </p>

      {showForm && (
        <form className="config-form" onSubmit={handleSubmit}>
          <div className="config-form-field">
            <label htmlFor="bonus-model-name">Name</label>
            <input
              id="bonus-model-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Weekend double"
            />
          </div>
          <div className="config-form-field">
            <label htmlFor="bonus-day-start">Bonus day starts at</label>
            <input
              id="bonus-day-start"
              type="time"
              value={form.startTime}
              onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
            />
          </div>
          {form.tiers.map((tier, index) => (
            <fieldset className="config-tier-fieldset" key={index}>
              <legend>Tier {index + 1}</legend>
              <div className="config-form-row">
                <div className="config-form-field">
                  <label htmlFor={`bonus-tier${index + 1}-start`}>Start price (€)</label>
                  <input
                    id={`bonus-tier${index + 1}-start`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={tier.startPrice}
                    onChange={(e) => updateTier(index, 'startPrice', e.target.value)}
                  />
                </div>
                {index === 2 && (
                  <div className="config-form-field">
                    <label htmlFor="bonus-tier3-end">End price (€)</label>
                    <input
                      id="bonus-tier3-end"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.tier3EndPrice}
                      onChange={(e) => setForm((prev) => ({ ...prev, tier3EndPrice: e.target.value }))}
                    />
                  </div>
                )}
                <div className="config-form-field">
                  <label htmlFor={`bonus-tier${index + 1}-bonus`}>Credit bonus</label>
                  <input
                    id={`bonus-tier${index + 1}-bonus`}
                    type="number"
                    min="0"
                    step="1"
                    value={tier.creditBonus}
                    onChange={(e) => updateTier(index, 'creditBonus', e.target.value)}
                  />
                </div>
                <div className="config-form-field">
                  <label htmlFor={`bonus-tier${index + 1}-color`}>Color</label>
                  <input
                    id={`bonus-tier${index + 1}-color`}
                    type="color"
                    value={tier.color}
                    onChange={(e) => updateTier(index, 'color', e.target.value)}
                  />
                </div>
              </div>
            </fieldset>
          ))}
          {formError && (
            <p className="config-form-error" role="alert">
              {formError}
            </p>
          )}
          <div className="config-form-actions">
            <button type="button" className="config-ghost-btn" onClick={closeForm}>
              Cancel
            </button>
            <button type="submit" className="config-primary-btn" disabled={saving}>
              {saving ? 'Creating…' : 'Create bonus model'}
            </button>
          </div>
        </form>
      )}

      {bonusConfigurationsLoading ? (
        <p className="config-muted">Loading bonus models…</p>
      ) : bonusConfigurations.length === 0 ? (
        <p className="config-muted">No bonus models yet.</p>
      ) : (
        <div className="config-mode-cards">
          {bonusConfigurations.map((model) => (
            <BonusModelCard key={model.id} model={model} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </ConfigSection>
  );
};

export default BonusTiersSection;
